<template>
  <Teleport to="body">
    <transition name="drawer-fade">
      <!-- Backdrop dismissal is supplementary; the drawer has a keyboard-operable close button. -->
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
      <div v-if="modelValue" class="drawer-panel__backdrop" data-qa="drawer-panel-backdrop" @mousedown="close" />
    </transition>
    <transition name="drawer-slide">
      <div
        v-if="modelValue"
        class="drawer-panel"
        data-qa="drawer-panel"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel ?? title"
        @mousedown.stop
      >
        <div class="drawer-panel__header">
          <div class="drawer-panel__nav-row">
            <div class="drawer-panel__nav-spacer" />
            <button
              class="drawer-panel__close"
              :aria-label="`Close ${title}`"
              data-qa="drawer-panel-close"
              type="button"
              @click="close"
            >
              <img src="@/assets/icons/lightbox-cross-small.svg" alt="" aria-hidden="true" />
            </button>
          </div>
          <div class="drawer-panel__title-row">
            <h2 class="drawer-panel__title">{{ title }}</h2>
            <span v-if="badge && badge > 0" class="drawer-panel__badge" data-qa="drawer-panel-badge">
              {{ badge }}
            </span>
          </div>
        </div>

        <div class="drawer-panel__body">
          <slot />
        </div>

        <div v-if="$slots.footer" class="drawer-panel__footer">
          <slot name="footer" />
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useScrollLock } from '@/utils/useScrollLock';

interface Props {
  modelValue: boolean;
  title: string;
  badge?: number;
  ariaLabel?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const isOpen = computed(() => props.modelValue);
useScrollLock(isOpen);

const close = () => {
  emit('update:modelValue', false);
};
</script>

<style scoped lang="scss">
.drawer-panel {
  --drawer-safe-area-bottom: 0px;
  --drawer-safe-area-bottom: env(safe-area-inset-bottom, 0px);
  position: fixed;
  bottom: var(--drawer-safe-area-bottom);
  left: 0;
  right: 0;
  z-index: 14;
  display: flex;
  flex-direction: column;
  max-height: calc(85vh - var(--drawer-safe-area-bottom));
  background: $brand-primary-100-white;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 24px $black-rgb-25;
  overflow: hidden;

  @include new-ms {
    --drawer-safe-area-bottom: 0px;
    top: 0;
    right: 0;
    bottom: 0;
    left: auto;
    width: 512px;
    max-height: 100vh;
    border-radius: 0;
  }

  &__backdrop {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 13;
    background-color: $black-rgb-25;
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 15px 24px 5px;
    flex-shrink: 0;
  }

  &__nav-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 44px;
  }

  &__nav-spacer {
    flex: 1;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__title {
    color: $brand-primary-100-navy;
    font-family: "Museo Slab";
    font-size: remc(24px);
    font-weight: 700;
    line-height: remc(32px);
    margin: 0;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 34px;
    background: $brand-secondary-100-red;
    color: $brand-primary-100-white;
    font-size: remc(12px);
    font-weight: 700;
    line-height: 1;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: $brand-primary-100-navy;
  }

  &__body {
    flex: 1;
    overflow-y: auto;
  }

  &__footer {
    padding: 16px;
    border-top: 1px solid $light-navy-10;
    flex-shrink: 0;
  }
}

.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateY(calc(100% + var(--drawer-safe-area-bottom)));

  @include new-ms {
    transform: translateX(100%);
  }
}
</style>
